import { C4_COLS, C4_ROWS, type C4State, c4Index, type Role, type Seat } from '@xox/shared'
import { motion } from 'motion/react'
import { type KeyboardEvent, useState } from 'react'

interface Props {
  game: C4State
  role: Role
  canPlay: boolean
  /** Column just sent and not yet answered by the server. */
  pending: number | null
  onMove: (col: number) => void
}

const DISC: Record<Seat, string> = {
  X: 'bg-[var(--c4-red)]',
  O: 'bg-[var(--c4-yellow)]',
}
const DISC_NAME: Record<Seat, string> = { X: 'red', O: 'yellow' }

/**
 * The 7×6 frame. Whole columns are the hit targets (tap, click, or arrow keys + Enter); a ghost
 * disc previews where the drop would land and each disc falls in from the top when it arrives.
 */
export function C4Board({ game, role, canPlay, pending, onMove }: Props) {
  const [hover, setHover] = useState<number | null>(null)
  const [focusCol, setFocusCol] = useState(3)
  const winSet = new Set<number>(game.winLine ?? [])
  const previewCol = canPlay ? (pending ?? hover) : null
  const lastIdx =
    game.lastCol === null ? null : c4Index(game.heights[game.lastCol]! - 1, game.lastCol)

  const statusText =
    game.status === 'playing'
      ? role === 'spectator'
        ? `${DISC_NAME[game.turn]} to move`
        : game.turn === role
          ? 'Your turn'
          : `${DISC_NAME[game.turn]} is thinking`
      : game.winner
        ? `${DISC_NAME[game.winner]} wins`
        : 'Draw'

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
    if (!delta) return
    e.preventDefault()
    const next = (focusCol + delta + C4_COLS) % C4_COLS
    setFocusCol(next)
    document.getElementById(`c4-col-${next}`)?.focus()
  }

  return (
    <div
      className="relative rounded-3xl bg-[var(--c4-frame)] p-2 shadow-[inset_0_-6px_0_var(--c4-frame-dark)] sm:p-3"
      style={{ width: 'min(92vw, 68dvh, 560px)' }}
      role="group"
      aria-label={`Connect Four board. ${statusText}`}
    >
      <div className="grid grid-cols-7 gap-1.5 sm:gap-2" aria-hidden>
        {Array.from({ length: C4_ROWS }, (_, fromTop) => {
          const row = C4_ROWS - 1 - fromTop
          return Array.from({ length: C4_COLS }, (_, col) => {
            const idx = c4Index(row, col)
            const disc = game.board[idx]
            const ghost = previewCol === col && disc === null && game.heights[col] === row
            return (
              <div
                key={idx}
                className="relative aspect-square rounded-full bg-[var(--c4-hole)] shadow-[inset_0_2px_4px_rgba(0,0,0,.35)]"
              >
                {disc && (
                  <motion.div
                    key={`${idx}-${disc}`}
                    initial={{ y: `-${(fromTop + 1) * 110}%`, opacity: 0.9 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 24, mass: 0.9 }}
                    className={[
                      'absolute inset-[7%] rounded-full shadow-[inset_0_-3px_0_rgba(0,0,0,.25)]',
                      DISC[disc],
                      winSet.has(idx) ? 'ring-4 ring-white animate-pulse-slow' : '',
                      idx === lastIdx && !winSet.has(idx) ? 'ring-2 ring-white/60' : '',
                    ].join(' ')}
                  >
                    <span className="absolute top-[14%] left-[20%] size-[28%] rounded-full bg-white/25" />
                  </motion.div>
                )}
                {ghost && (
                  <div
                    className={`absolute inset-[7%] rounded-full opacity-40 ${DISC[game.turn]} ${pending === col ? 'animate-pulse' : ''}`}
                  />
                )}
              </div>
            )
          })
        })}
      </div>
      {/* Column hit areas sit over the frame; each is one big button. */}
      <div
        role="group"
        aria-label="Columns"
        className="absolute inset-0 grid grid-cols-7 rounded-3xl"
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHover(null)}
      >
        {Array.from({ length: C4_COLS }, (_, col) => {
          const free = C4_ROWS - game.heights[col]!
          const playable = canPlay && free > 0
          return (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional by definition
              key={col}
              id={`c4-col-${col}`}
              type="button"
              tabIndex={col === focusCol ? 0 : -1}
              aria-label={`Drop in column ${col + 1}, ${free} free`}
              aria-disabled={!playable}
              onFocus={() => {
                setFocusCol(col)
                setHover(col)
              }}
              onMouseEnter={() => setHover(col)}
              onClick={() => playable && onMove(col)}
              className={`focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-white ${playable ? 'cursor-pointer' : 'cursor-default'}`}
            />
          )
        })}
      </div>
    </div>
  )
}
